import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { QUDA_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const TEAL = QUDA_COLORS.TEAL_DARK;

function fmtDate(iso) {
  if (!iso) return iso;
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
}

function StatCard({ label, value, sub }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        backgroundColor: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: TEAL, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function RankList({ title, items, color }) {
  if (!items || items.length === 0) return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color, mb: 1 }}>{title}</Typography>
      <Typography variant="body2" color="textSecondary">No data in the last 30 days</Typography>
    </Box>
  );
  const max = items[0]?.count ?? 1;
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color, mb: 1.5 }}>{title}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {items.map((item, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 16, textAlign: "right" }}>{i + 1}.</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{item.name || "—"}</Typography>
                <Typography variant="body2" sx={{ color, fontWeight: 700, ml: 1 }}>{item.count}</Typography>
              </Box>
              <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "#e5e7eb", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${(item.count / max) * 100}%`, backgroundColor: color, borderRadius: 2 }} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const ChemistQudaTab = ({ chemistUUID, isManager = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!chemistUUID || data) return;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(isManager ? API_PATHS.MANAGER_CHEMIST_QUDA : API_PATHS.CHEMIST_QUDA, "GET", null, { ChemistUUID: chemistUUID });
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load Quda data"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID, data]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
      <Typography color="textSecondary">Loading Quda data…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ py: 4, px: 2 }}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );

  if (!data) return null;

  const { Metrics, OrdersPerDay, TopItems, TopStaff, CategoryBreakdown } = data;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
        <StatCard
          label="Total Orders (30d)"
          value={Number(Metrics?.TotalOrders30d ?? 0).toLocaleString()}
        />
        <StatCard
          label="Avg / Day"
          value={Number(Metrics?.AvgOrdersPerDay ?? 0).toFixed(1)}
          sub="over last 30 days"
        />
        <StatCard
          label="Dispensary"
          value={`${Number(Metrics?.DispensaryPct ?? 0).toFixed(1)}%`}
          sub={`${Number(Metrics?.TotalDispensary30d ?? 0).toLocaleString()} orders`}
        />
        <StatCard
          label="OTC"
          value={`${(100 - Number(Metrics?.DispensaryPct ?? 0)).toFixed(1)}%`}
          sub={`${Number(Metrics?.TotalOtc30d ?? 0).toLocaleString()} orders`}
        />
        <StatCard
          label="All-time orders"
          value={Number(Metrics?.TotalOrdersAllTime ?? 0).toLocaleString()}
        />
      </Box>

      {/* Orders per day chart (left) + Category breakdown (right) — 50/50 */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TEAL, mb: 1 }}>
            Orders per day (30d)
          </Typography>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={OrdersPerDay} margin={{ top: 2, right: 4, left: -20, bottom: 18 }}>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 9 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={fmtDate}
                interval="preserveStartEnd"
                angle={-35}
                textAnchor="end"
              />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <ReTooltip
                formatter={(v, name) => [v, name === "dispensary" ? "Dispensary" : "OTC"]}
                labelFormatter={fmtDate}
                contentStyle={{ fontSize: 11 }}
              />
              <Legend
                formatter={(v) => v === "dispensary" ? "Dispensary" : "OTC"}
                iconSize={10}
                wrapperStyle={{ fontSize: 11 }}
              />
              <Bar dataKey="dispensary" stackId="a" fill={TEAL} radius={[0, 0, 0, 0]} />
              <Bar dataKey="otc" stackId="a" fill={QUDA_COLORS.TEAL_MID} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <RankList title="Orders by category (30d)" items={CategoryBreakdown} color={TEAL} />
      </Box>

      {/* Top items + staff lists */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <RankList title="Top 10 items ordered (30d)" items={TopItems} color={TEAL} />
        <RankList title="Top 10 staff by interactions (30d)" items={TopStaff} color="#7c3aed" />
      </Box>
    </Box>
  );
};

export default ChemistQudaTab;
